// components/DynamicForm.tsx
import React, { useMemo, useState, useEffect } from "react";
import { useForm, Controller } from "react-hook-form";
import {
  FieldDef,
  SHEET_FIELDS,
  SheetKey,
  INCOTERM_OPTIONS,
  sheetFromIncoterm,
} from "../config/formConfig";
import InputWithLabel from "./InputWithLabel";
import Dropdown from "./DropDown";
import Tooltip from "./Tooltip";
import { SuccessAlertBox } from "./SuccessAlertBox";
import {
  add_vendor_form,
  common_file_upload,
  update_vendor_form,
  get_vendor_form,
  vendor_upload_file,
} from "../constants/apis";
import { toast } from "react-toastify";
import { fileToBase64 } from "../utils";
import { json } from "react-router-dom";

type Incoterm = (typeof INCOTERM_OPTIONS)[number];

// Helper function to get nested error messages from dotted field names
function getError(obj: any, path: string): string | undefined {
  const keys = path.split(".");
  let result = obj;
  for (const key of keys) {
    result = result?.[key];
    if (!result) return undefined;
  }
  return typeof result?.message === "string" ? result.message : undefined;
}

function groupBySection(fields: FieldDef[]) {
  const map = new Map<string, FieldDef[]>();
  fields.forEach((f) => {
    if (!map.has(f.section)) map.set(f.section, []);
    map.get(f.section)!.push(f);
  });
  return map;
}

interface DynamicFormProps {
  incoterm: Incoterm | "";
  hash_id: string| null; // optional external hash id
  onSubmitData?: (data: any) => void; // make optional for safety
}

// Data transform for API (strictly matches desired payload shape)
function transformFormDataToAPI(formData: any, incoterm: string) {
  const s = (v: any) => (v === undefined || v === null ? "" : String(v));

  const mainFreight = s(formData?.freight?.mainFreight); // "Ocean" | "Air"

  return {
    incoterms: incoterm,

    general_information: {
      shipper: s(formData?.general?.shipper),
      consignee: s(formData?.general?.consignee),
      origin: s(formData?.general?.origin),
      destination: s(formData?.general?.destination),

      freight_type: s(formData?.general?.freightType),
      rate_validity: s(formData?.general?.rateValidity),
      free_days: s(formData?.general?.freeDays),
      volume: s(formData?.general?.volume),
    },

    freight: {
      mode: s(formData?.freight?.mainFreight),
      container_type: s(formData?.general?.freightType),
      rate: s((formData as any)?.freight?.rate),

      main_freight: mainFreight,
      carrier: s(
        formData?.freight?.carrier ??
          formData?.freight?.carrierEXW ??
          formData?.freight?.carrierDAP
      ),
      transit_time: s(
        formData?.freight?.transitTime ??
          formData?.freight?.transitTimeEXW ??
          formData?.freight?.transitTimeDAP
      ),
    },

    origin_charges: {
      handling: s(formData?.origin?.othc),
      documentation: s(formData?.origin?.doFeeOrigin),

      othc: s(formData?.origin?.othc),
      do_fee_origin: s(formData?.origin?.doFeeOrigin),
      customs_origin: s(formData?.origin?.customsOrigin),
      bl_charges: s(formData?.origin?.blCharges),
      export_doc_fee: s(formData?.origin?.exportDocFee),
      seal_fee: s(formData?.origin?.sealFee),
      other_origin: s(formData?.origin?.otherOrigin),
    },

    destination_charges: {
      customs: s(formData?.dest?.customsDest),
      delivery: s(formData?.dest?.doFeeDest),

      dthc: s(formData?.dest?.dthc),
      do_fee_dest: s(formData?.dest?.doFeeDest),
      customs_dest: s(formData?.dest?.customsDest),
      port_handling: s(formData?.dest?.portHandling),
      inland_delivery: s(formData?.dest?.inlandDelivery),
      other_dest: s(formData?.dest?.otherDest),
    },

    us_inland_freight: {
      pre_pull: s(formData?.us?.prePull),
      chassis_split: s(formData?.us?.chassisSplit),
      chassis_fee: s(formData?.us?.chassisFee),
      other_inland: s(formData?.us?.otherInland),
    },

    totals: s(formData?.totals?.allIn),
    other_charges: s(formData?.other?.tc),
    attachments: s(formData?.attachments?.vendorPdf),
  };
}

export const DynamicForm: React.FC<DynamicFormProps> = ({
  incoterm,
  hash_id,
  onSubmitData,
}) => {
  const sheet: SheetKey = incoterm ? sheetFromIncoterm(incoterm) : "FOB";
  const fields = SHEET_FIELDS[sheet];
  const [isSubmitting, setIsSubmitting] = useState(false);
  // const [searchParams] = useSearchParams();
  // const hashIdFromUrl = searchParams.get("hash_id");
  // console.log(hashIdFromUrl,"hassing");
  const [showSuccess, setShowSuccess] = useState(false);
  const [successMsg, setSuccessMsg] = useState<string>("");
  const [serverId, setServerId] = useState<string | null>(null);
  const {
    control,
    register,
    handleSubmit,
    formState: { errors, isSubmitted },
    watch,
    getValues,
    reset,
    setValue,
  } = useForm({
    mode: "onSubmit",
    // defaultValues: {
    //   general: {},
    //   freight: {},
    //   origin: {},
    //   dest: {},
    //   us: {},
    //   totals: {},
    //   other: {},
    //   attachments: {},
    // },
  });

  const sections = useMemo(() => groupBySection(fields), [fields]);
  const costFieldIds = useMemo(
    () =>
      fields
        .filter(
          (f) =>
            f.type === "number" &&
            f.section !== "Totals" &&
            !f.id.includes("transitTime")
        )
        .map((f) => f.id),
    [fields]
  );
  const costValues = watch(costFieldIds);

  // Auto-calculate totals
  useEffect(() => {
    const sum = (Array.isArray(costValues) ? costValues : []).reduce(
      (acc: number, v: unknown) => {
        const n = typeof v === "string" ? parseFloat(v) : (v as number);
        return acc + (Number.isFinite(n) ? (n as number) : 0);
      },
      0
    );
    const totalField = fields.find(
      (f) => f.section === "Totals" && f.id.includes("allIn")
    );
    if (totalField) {
      const currentTotal = watch(totalField.id);
      const newTotal = Number(sum.toFixed(2));
      if (Number(currentTotal) !== newTotal) {
        setValue(totalField.id, newTotal, {
          shouldDirty: true,
          shouldValidate: true,
        });
      }
    }
  }, [costValues, fields, watch, setValue]);

  // Prefill form when hash_id exists by fetching existing data
  useEffect(() => {
    const loadExisting = async () => {
      console.log("hash_id", hash_id);
      if (!hash_id) return;
      try {
        const res = await fetch(get_vendor_form, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            hash_id: hash_id,
          }),
        });
        if (!res.ok) return;
        const payload = await res.json();
        const data = payload?.data || payload;
        if (!data) return;
        // Top-level: incoterms (not a form input directly)
        // general_information
         setServerId(data?.id)
        if (data?.general_information) {
          const g = data?.general_information;
          setValue("general.shipper" as any, g.shipper || "");
          setValue("general.consignee" as any, g.consignee || "");
          setValue("general.origin" as any, g.origin || "");
          setValue("general.destination" as any, g.destination || "");
          setValue("general.freightType" as any, g.freight_type || []);
          setValue("general.rateValidity" as any, g.rate_validity || "");
          setValue("general.freeDays" as any, g.free_days || "");
          setValue("general.volume" as any, Number(g.volume) || "");
        }
        // freight
        if (data?.freight) {
          const f = data?.freight;
          setValue("freight.mainFreight" as any, f.main_freight || "");
          setValue("freight.carrier" as any, f.carrier || "");
          setValue("freight.transitTime" as any, f.transit_time || "");
        }
        // origin_charges
        if (data?.origin_charges) {
          const o = data?.origin_charges;
          setValue("origin.othc" as any, o.othc || o.handling || 0);
          setValue(
            "origin.doFeeOrigin" as any,
            o.do_fee_origin || o.documentation || ""
          );
          setValue("origin.customsOrigin" as any, o.customs_origin || "");
          setValue("origin.blCharges" as any, o.bl_charges || "");
          setValue("origin.exportDocFee" as any, o.export_doc_fee || "");
          setValue("origin.sealFee" as any, o.seal_fee || "");
          setValue("origin.otherOrigin" as any, Number(o.other_origin) || "");
        }
        // destination_charges
        if (data?.destination_charges) {
          const d = data?.destination_charges;
          setValue("dest.dthc" as any, d.dthc || "");
          setValue("dest.doFeeDest" as any, d.do_fee_dest || d.delivery || "");
          setValue(
            "dest.customsDest" as any,
            d.customs_dest || d.customs || ""
          );
          setValue("dest.portHandling" as any, d.port_handling || "");
          setValue("dest.inlandDelivery" as any, Number(d.inland_delivery) || "");
          setValue("dest.otherDest" as any, d.other_dest || "");
        }
        // us_inland_freight
        if (data?.us_inland_freight) {
          const u = data?.us_inland_freight;
          setValue("us.prePull" as any, u.pre_pull || "");
          setValue("us.chassisSplit" as any, u.chassis_split || "");
          setValue("us.chassisFee" as any, u.chassis_fee || "");
          setValue("us.otherInland" as any, u.other_inland || "");
        }
        // totals
        if (data.totals) {
          setValue("totals.allIn" as any, data.totals.all_in || "");
        }
        // other_charges
        if (data.other_charges) {
          const oc = data.other_charges;
          setValue("other.tc" as any, oc.tc || oc || "");
        }
        // attachments
        if (data.attachments) {
          const at = data.attachments;
          setValue("attachments.vendorPdf" as any, at.vendor_pdf || at || "");
        }
        if (payload?.id) setServerId(String(payload.id));
      } catch (e) {
        // ignore load errors for now
      }
    };
    loadExisting();
  }, [hash_id, setValue,incoterm]);

  const uploadFile = async (file: File) => {
    try {
      const base64String = await fileToBase64(file);

      const res = await fetch(vendor_upload_file, {
        method: "POST",
        headers: {
          // Authorization: `Bearer ${jwtToken}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          base64File: base64String,
          hash_id:hash_id,
        }),
      });

      const data = await res.json();

     if (data?.status === "success" && typeof data.url === "string") {
      // Mirror URL into form state
      setValue("attachments.vendorPdf" as any, data.url, {
        shouldDirty: true,
        shouldValidate: true,
      });
      return data.url;
    }
    throw new Error(data?.msg || "File upload failed");
    } catch (error) {
      console.error("Error converting file to Base64:", error);
      // toast.error("File upload went wrong please try again");
    }
  };

  /** Submit handler */
  const onSubmit = async (data?: any) => {
    console.log("[DynamicForm] Submit valid. Complete form data:", data);
    console.log("[DynamicForm] Selected sheet:", sheet);

    setIsSubmitting(true);
    try {
      const apiPayload = transformFormDataToAPI(data, sheet);
      console.log("[DynamicForm] API Payload:", apiPayload);
      if (serverId) {
        const response = await fetch(update_vendor_form, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            hash_id,
            id: serverId,
            ...apiPayload,
          }),
        });
        const result = await response.json().catch(() => ({}));
        if (response.ok) {
          // Expecting { id, status, message }
          if (result?.id) setServerId(String(result.id));
          if (result?.message) setSuccessMsg(String(result.message));
          else setSuccessMsg("Form submitted successfully.");
          setShowSuccess(true);
          if (onSubmitData) {
            onSubmitData({ sheet, data: result });
          }
        } else {
          const message = result?.message || "Unknown error";
          alert(`Failed to submit quote: ${message}`);
        }

      } else {
        const response = await fetch(add_vendor_form, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            hash_id,
            ...apiPayload,
          }),
        });
        const result = await response.json().catch(() => ({}));
        if (response.ok) {
          // Expecting { id, status, message }
          if (result?.id) setServerId(String(result.id));
          if (result?.message) setSuccessMsg(String(result.message));
          else setSuccessMsg("Form submitted successfully.");
          setShowSuccess(true);
          if (onSubmitData) {
            onSubmitData({ sheet, data: result });
          }
        } else {
          const message = result?.message || "Unknown error";
          alert(`Failed to submit quote: ${message}`);
        }
      }
    } catch (error) {
      alert("Network error! Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleFileChangeAndUpload = async (
    e: React.FormEvent<HTMLInputElement>
  ) => {
    const file = e.currentTarget.files?.[0];

    if (file) {
      await uploadFile(file);
    }
  };

  useEffect(() => {
    reset();
  }, [incoterm, reset]);
  useEffect(() => {
    // Sum all numeric fields (excluding Totals and transit times) from watched values
    const sum = (Array.isArray(costValues) ? costValues : []).reduce(
      (acc: number, v: unknown) => {
        const n = typeof v === "string" ? parseFloat(v) : (v as number);
        return acc + (Number.isFinite(n) ? (n as number) : 0);
      },
      0
    );
    const totalField = fields.find(
      (f) => f.section === "Totals" && f.id.includes("allIn")
    );
    if (totalField) {
      const currentTotal = watch(totalField.id);
      const newTotal = Number(sum.toFixed(2));
      if (Number(currentTotal) !== newTotal) {
        setValue(totalField.id as any, newTotal, {
          shouldDirty: true,
          shouldValidate: true,
        } as any);
      }
    }
  }, [costValues, fields, watch, setValue]);

  // Optional: GET and UPDATE handlers omitted for clarity (add if you need to load/update quotes)

  /** Field renderer */
  const renderField = (f: FieldDef) => {
    const fieldError =
      f.required && isSubmitted ? getError(errors, f.id) : undefined;

    // SELECT
    if (f.type === "select") {
      const options = f.options || [];
      return (
        <div key={f.id}>
          <Controller
            control={control}
            name={f.id}
            rules={{ required: f.required ? f.errMsg || "Required" : false }}
            render={({ field }) => (
              <Dropdown
                label={incoterm === "AIR" && f.id === "general.freightType" ?"Please Select Same as Incoterms":f.label}
                options={incoterm === "AIR" ? [{label:"AIR",value:"AIR"}]:options}
                placeholder={
                  f.id === "general.freightType"
                    ? "Please select freight type"
                    : f.id === "freight.mainFreight"
                    ? "Please select main freight"
                    : undefined
                }
                onChange={(e) => {
                  const value = e?.currentTarget?.value || e; // Support different Dropdown signatures
                  field.onChange(value);
                  console.log(value);
                }}
              />
            )}
          />
          {fieldError && (
            <div style={{ color: "crimson", fontSize: 12 }}>{fieldError}</div>
          )}
        </div>
      );
    }

    if (f.type === "date") {
      return (
        <div key={f.id}>
          <Controller
            control={control}
            name={f.id}
            rules={{ required: f.required ? f.errMsg || "Required" : false }}
            render={({ field }) => (
              <InputWithLabel
                label={f.label}
                type="date"
                onChange={(e) => {
                  const value = e?.currentTarget?.value || e; // Support different Dropdown signatures
                  field.onChange(value);
                  console.log(value);
                }}
              />
            )}
          />
          {fieldError && (
            <div style={{ color: "crimson", fontSize: 12 }}>{fieldError}</div>
          )}
        </div>
      );
    }

    // NUMBER
    if (f.type === "number") {
      const reg = register(f.id, {
        required: f.required ? f.errMsg || "Required" : false,
        valueAsNumber: true,
      });
      return (
        <div key={f.id} className="w-full mb-2">
          <InputWithLabel
            label={f.label}
            type="text"
            inputMode="numeric"
            pattern="[0-9]*"
            element={f.note && <Tooltip content={f.note} />}
            error={fieldError}
            {...reg}
            onChange={(e) => {
              reg.onChange(e);
            }}
          />
        </div>
      );
    }

    // TEXTAREA
    if (f.type === "textarea") {
      const reg = register(f.id, {
        required: f.required ? f.errMsg || "Required" : false,
      });
      return (
        <div key={f.id} style={{ marginBottom: 12 }}>
          <InputWithLabel
            label={f.label}
            type="text"
            element={f.note && <Tooltip content={f.note} />}
            error={fieldError}
            {...reg}
            onChange={(e) => {
              reg.onChange(e);
            }}
          />
        </div>
      );
    }

    // FILE
    if (f.type === "file") {
      //  const { onChange: rhfOnChange, ...fileReg } = register(f.id);
       const url = getValues("attachments.vendorPdf") as string | undefined;
      return (
        <div key={f.id} className="!grid !grid-cols-1" style={{ marginBottom: 12 }}>
          {url ? (
        <div className="mb-2 flex justify-end">
          <a
            className="text-custom-blue border border-custom-blue px-3 py-1 rounded-md"
            target="_blank"
            rel="noreferrer"
            href={`https://taram.io/new/apis/vendor_quotes_form/view_file.php?file=${url}`}
          >
            View Uploaded File
          </a>
        </div>
      ) : null}
          <InputWithLabel
            label={f.label}
            type="file"
            element={f.note && <Tooltip content={f.note} />}
            error={fieldError}
            {...register(f.id)}
            onChange={(e) => {
              handleFileChangeAndUpload(e);
              toast.promise(handleFileChangeAndUpload(e), {
                pending: "File is uploading",
                success: "File has been uploaded 👌",
                error: "Rejected, please try again 🤯",
              });
            }}
          />
        </div>
      );
    }

    // DEFAULT TEXT
    {
      const reg = register(f.id, {
        required: f.required ? f.errMsg || "Required" : false,
      });
      return (
        <div key={f.id} style={{ marginBottom: 12 }}>
          <InputWithLabel
            label={f.label}
            type="text"
            element={f.note && <Tooltip content={f.note} />}
            error={fieldError}
            {...reg}
            onChange={(e) => {
              reg.onChange(e);
            }}
          />
        </div>
      );
    }
  };

  const onInvalid = (errs: any) => {
    console.warn(
      "[DynamicForm] Submit invalid. Validation errors found:",
      errs
    );
    Object.keys(errs).forEach((key) => {
      console.log(`  - ${key}: ${errs[key]?.message || "Unknown error"}`);
    });
  };

  return (
    <div
      className="card !bg-white !p-10 w-max-4xl mb-2"
      style={{ position: "relative" }}
    >
      <form onSubmit={handleSubmit(onSubmit, onInvalid)}>
        <span className="border-custom-blue-new rounded-full border-2 text-custom-blue-new p-2">
          {sheet} Quotes
        </span>
        {[...sections.keys()].map((section) => (
          <div key={section}>
            <legend className="font-semibold mt-5 mb-2 border-b pb-2 border-slate-100">
              {section}
            </legend>
            <fieldset className={section === "Attachments"|| section === "Other charges & TC" ||section === "Totals" ? "grid grid-cols-1":"grid grid-cols-1 md:grid-cols-2 gap-4"}>
              {(sections.get(section) || []).map(renderField)}
            </fieldset>
          </div>
        ))}
        <button
          type="submit"
          className="w-full h-[44px] mt-10 rounded-md bg-custom-blue-medium text-white font-medium"
          disabled={isSubmitting}
        >
          {isSubmitting ? "Saving..." : "Save"}
        </button>
      </form>
      {showSuccess && (
        <SuccessAlertBox
          isOpen={showSuccess}
          headerText={successMsg || "Form has been submitted successfully"}
          
          // subText=" You can close this dialog now."
          onClose={() => setShowSuccess(false)} 
         />
      )}
    </div>
  );
};
